import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { TableSkeleton } from '@/components/ui/skeleton';
import { quotationColumns } from './quotation-table-columns';
import { QuotationStatus, type QuotationListItemDto } from '../types/quotation.types';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface QuotationTableProps {
  data: QuotationListItemDto[];
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  pagination: PaginationState;
  onPaginationChange: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  pageCount: number;
  onRowClick: (row: QuotationListItemDto) => void;
}

export function QuotationTable({
  data,
  isLoading,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  pageCount,
  onRowClick,
}: QuotationTableProps) {
  const table = useReactTable({
    data,
    columns: quotationColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    autoResetPageIndex: false,
    state: { sorting, pagination },
    onSortingChange,
    onPaginationChange,
  });

  const { pageIndex, pageSize } = pagination;
  const canPrev = pageIndex > 0;
  const canNext = pageIndex < pageCount - 1;

  const hasRows = table.getRowModel().rows.length > 0;
  const showRefetchOverlay = isLoading && hasRows;

  return (
    <div className="space-y-4">
      <div className="relative">
        {showRefetchOverlay && (
          <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-md border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading
          </div>
        )}
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <TableHead key={h.id}>
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className={showRefetchOverlay ? 'opacity-60 pointer-events-none' : undefined}>
            {hasRows ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onRowClick(row.original)}
                  title={
                    row.original.status === QuotationStatus.Submitted
                      ? 'View submitted quotation'
                      : 'Open draft'
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isLoading ? (
              <TableRow>
                <TableCell colSpan={quotationColumns.length} className="p-4">
                  <TableSkeleton rows={8} cols={quotationColumns.length} />
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={quotationColumns.length} className="h-24 text-center">
                  No quotations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) =>
              onPaginationChange((prev) => ({ ...prev, pageSize: Number(value), pageIndex: 0 }))
            }
            disabled={isLoading}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Page {pageCount === 0 ? 0 : pageIndex + 1} of {pageCount}
          </span>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPaginationChange((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))}
              disabled={!canPrev || isLoading}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPaginationChange((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))}
              disabled={!canNext || isLoading}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
